import React, { Component, type ReactNode, type ErrorInfo } from 'react';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Paper,
  Typography,
} from '@mui/material';
import { Refresh, BugReport } from '@mui/icons-material';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
  level?: 'page' | 'component' | 'critical';
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error: Error): State {
    return {
      hasError: true,
      error,
      errorInfo: null,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    this.setState({
      error,
      errorInfo,
    });

    // Call custom error handler if provided
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }

    // Log error to console in development
    if (process.env.NODE_ENV === 'development') {
      console.error('ErrorBoundary caught an error:', error, errorInfo);
    }

    // Here you could also log to an external service like Sentry
    // logErrorToService(error, errorInfo);
  }

  private handleRetry = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
  };

  private renderFallback() {
    const { fallback, level = 'component' } = this.props;
    const { error } = this.state;

    if (fallback) {
      return fallback;
    }

    // Different fallback UIs based on error level
    switch (level) {
      case 'critical':
        return (
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            minHeight="100vh"
            p={3}
          >
            <Paper
              elevation={3}
              sx={{ p: 4, textAlign: 'center', maxWidth: 500 }}
            >
              <BugReport sx={{ fontSize: 64, color: '#F3776D', mb: 2 }} />
              <Typography variant="h4" gutterBottom color="#F3776D">
                Critical Error
              </Typography>
              <Typography variant="body1" paragraph color="text.secondary">
                The application encountered a critical error and needs to
                reload.
              </Typography>
              <Button
                variant="contained"
                startIcon={<Refresh />}
                onClick={() => window.location.reload()}
                sx={{
                  backgroundColor: 'var(--liftstatus-primary-color)',
                  '&:hover': {
                    backgroundColor: 'var(--liftstatus-primary-color-dark)',
                  },
                }}
              >
                Reload Application
              </Button>
            </Paper>
          </Box>
        );

      case 'page':
        return (
          <Box p={3}>
            <Alert
              severity="error"
              action={
                <Button size="small" onClick={this.handleRetry}>
                  Try Again
                </Button>
              }
            >
              <AlertTitle>Page Error</AlertTitle>
              Something went wrong loading this page. Please try again.
              {process.env.NODE_ENV === 'development' && error && (
                <Box mt={2}>
                  <Typography variant="caption" component="pre">
                    {error.message}
                  </Typography>
                </Box>
              )}
            </Alert>
          </Box>
        );

      case 'component':
      default:
        return (
          <Alert
            severity="error"
            sx={{ m: 1 }}
            action={
              <Button size="small" onClick={this.handleRetry}>
                Retry
              </Button>
            }
          >
            Component failed to load
          </Alert>
        );
    }
  }

  render() {
    if (this.state.hasError) {
      return this.renderFallback();
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
